import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { router } from "./router.tsx";
import { RouterProvider } from "react-router-dom";
import "./i18n";
import { sentryErrorsToIgnore } from "shared/constants/sentry";

Sentry.init({
  dsn: "https://05789cb0ea97d40694e658c7111816a9@o4506431460343808.ingest.us.sentry.io/4508088573296640",
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/next\.whereabouts\.tech/,
    /^https:\/\/next-dev\.whereabouts\.tech/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment:
    import.meta.env.VITE_ENVIRONMENT !== "production"
      ? "development"
      : "production",
  enabled: import.meta.env.VITE_ENVIRONMENT !== "local",
  /**
   * We can supress certain error from being sent to Sentry
   * - For example, the ErrorBoundary that surrounds the CloudinaryImage
   *   component catches a "No Image" error if null/undefined is passed to the
   *   component. That error doesn't need to be sent to Sentry because it is a
   *   valid state for new entities that have not yet uploaded an image.
   * - The errors to ignore are stored in /src/constants in sentryErrorsToIgnore.
   * @param event
   * @returns
   */
  beforeSend: (event: Sentry.ErrorEvent) => {
    const errorMessages = event.message
      ? [event.message]
      : event.exception?.values?.map((item) => item.value);
    if (
      Object.values(sentryErrorsToIgnore).find((value) =>
        errorMessages?.includes(value),
      )
    ) {
      return null;
    }
    return event;
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
